import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { createPlayground, type EmbedOptions } from 'livecodes';
import type { Tutorial } from './api';
import confetti from 'canvas-confetti';
import styles from './PlaygroundEditor.module.css';

interface PlaygroundEditorProps {
  tutorial: Tutorial;
  onTestsPass?: () => void;
}

export interface PlaygroundEditorRef {
  updatePlaygroundContent: (tutorial: Tutorial) => Promise<void>;
}

const PlaygroundEditor = forwardRef<PlaygroundEditorRef, PlaygroundEditorProps>(({ tutorial, onTestsPass }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playgroundRef = useRef<any>(null);
  const [showStart, setShowStart] = useState(true);

  // Expose updatePlaygroundContent method via ref
  useImperativeHandle(ref, () => ({
    updatePlaygroundContent: async (newTutorial: Tutorial) => {
      if (playgroundRef.current && playgroundRef.current.setConfig) {
        try {
          await playgroundRef.current.setConfig({
            markup: {
              language: 'html',
              content: newTutorial.initial_code.html || '',
            },
            style: {
              language: 'css',
              content: newTutorial.initial_code.css || '',
            },
            script: {
              language: 'javascript',
              content: newTutorial.initial_code.js || '',
            },
            tests: {
              language: 'javascript',
              content: newTutorial.initial_code.test || '',
            },
          });
          console.log('Playground content updated successfully');
        } catch (error) {
          console.error('Error updating playground content:', error);
        }
      }
    }
  }));

  // Create the playground only once when the component mounts.
  useEffect(() => {
    let isMounted = true;

    const handleResize = () => {
      if (playgroundRef.current?.app?.workspace?.updateLayout) {
        playgroundRef.current.app.workspace.updateLayout();
      }
    };

    async function initPlayground() {
      if (!containerRef.current) return;

      const options: EmbedOptions = {
        container: containerRef.current,
        height: '100%',
        appUrl: 'https://playground-dtr.pages.dev/',
        params: { 
          console: 'open',
          disabledFeatures: ['web-share']
        },
        config: {
          markup: {
            language: 'html',
            content: tutorial.initial_code.html || '',
          },
          style: {
            language: 'css',
            content: tutorial.initial_code.css || '',
          },
          script: {
            language: 'javascript',
            content: tutorial.initial_code.js || '',
          },
          tests: {
            language: 'javascript',
            content: tutorial.initial_code.test || '',
          },
        },
      };

      try {
        const playground = await createPlayground(containerRef.current, options);
        if (isMounted) {
          playgroundRef.current = playground;
          
          // Use the LiveCodes API events
          playground.watch('tests', (result: any) => {
            console.log('Test results:', result);
            // If there are no tests, or all tests passed
            if (!tutorial.initial_code.test || (result && result.pass === result.total)) {
              console.log('Tests passed or no tests present, calling onTestsPass');
              onTestsPass?.();
            }
          });

          // Watch for load events
          playground.watch('load', () => {
            console.log('Playground loaded');
          });

          // Watch for run events
          playground.watch('run', () => {
            console.log('Code running...');
            if (!tutorial.initial_code.test) {
              console.log('No tests present, calling onTestsPass');
              onTestsPass?.();
            }
          });
        }
      } catch (error) {
        console.error('Error initializing playground:', error);
      }
    }

    initPlayground();

    // Add resize event listener
    window.addEventListener('resize', handleResize);
    
    // Force initial layout update
    setTimeout(handleResize, 100);

    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);
      if (playgroundRef.current && playgroundRef.current.destroy) {
        playgroundRef.current.destroy();
      }
    };
  }, []); // Only run once on mount

  useEffect(() => {
    const handleResize = () => {
      if (playgroundRef.current?.resize) {
        playgroundRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update content when tutorial changes
  useEffect(() => {
    if (playgroundRef.current && tutorial) {
      console.log('Updating playground content for tutorial:', tutorial.slug);
      playgroundRef.current.setConfig({
        markup: {
          language: 'html',
          content: tutorial.initial_code.html || '',
        },
        style: {
          language: 'css',
          content: tutorial.initial_code.css || '',
        },
        script: {
          language: 'javascript',
          content: tutorial.initial_code.js || '',
        },
        tests: {
          language: 'javascript',
          content: tutorial.initial_code.test || '',
        },
      }).then(() => {
        // Force a resize event after the config is set
        window.dispatchEvent(new Event('resize'));
        console.log('Playground content updated successfully for tutorial:', tutorial.slug);
      }).catch((error: any) => {
        console.error('Error updating playground content:', error);
      });
    }
  }, [tutorial]); // Update only when tutorial changes

  return (
    <div className="flex flex-col h-full min-h-0 max-h-full w-full">
      <div ref={containerRef} className="flex-1 min-h-0 max-h-full w-full relative overflow-hidden">
        {showStart && (
          <div className="absolute inset-0 flex items-center justify-center bg-base-300 z-10">
            <button
              onClick={() => setShowStart(false)}
              className="btn btn-primary"
            >
              Start
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

PlaygroundEditor.displayName = 'PlaygroundEditor';

export default PlaygroundEditor;